/* eslint-disable react/no-unknown-property */
import { TextCaption } from '@pypestream/design-system';
import { LoaderStatus } from '@pypestream/pype-loader';
import { FC, useEffect, useState } from 'react';

import { useLivePype } from '../../components/test-surface';
import { useCallTranscript } from './use-call-transcript';
import type { RunChannel, ValidateTurn } from './validate-runs';

/** The chat starts a beat before the widget reports it; read logs from a little earlier. */
const LOG_LEAD_MS = 10_000;

/**
 * Validate's "Widget" mode (`pype-next`): the channel's real Pype Next
 * widget, as a customer sees it — its design, buttons, carousels and
 * webviews. The widget doesn't report its messages to the page, so the
 * conversation is read back from the engine logs by its chat id, the way a
 * "Call me" call is, and saved and scored as a Test run through the same
 * `onCall`.
 */
export const WidgetChat: FC<{
  channel: RunChannel;
  nodeMessages: ReadonlyMap<number, string>;
  onChat?: (chat: {
    chatId: string;
    transcript: ValidateTurn[];
    startedAt: number;
    done: boolean;
  }) => void;
  /** What has been read so far, for the score bar above. */
  onProgress?: (state: { chatId: string | null; done: boolean }) => void;
}> = ({ channel, nodeMessages, onChat, onProgress }) => {
  const { containerRef, status, error, loaderStatus, session } = useLivePype(
    channel.appId,
    channel.env
  );
  const chatId = session?.chatId ?? null;

  // Each new conversation (a restart in the widget) is its own run.
  const [startedAt, setStartedAt] = useState<number | undefined>();
  useEffect(() => {
    setStartedAt(chatId ? Date.now() - LOG_LEAD_MS : undefined);
  }, [chatId]);

  const view = useCallTranscript({
    integratorId: null,
    knownChatId: chatId,
    placedAt: startedAt,
    live: loaderStatus === LoaderStatus.ChatActive,
    nodeMessages,
  });

  const { transcript, done } = view;
  useEffect(() => {
    onProgress?.({ chatId, done });
    if (!chatId || !startedAt || !transcript.length) return;
    onChat?.({ chatId, transcript, startedAt, done });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, transcript, done, startedAt]);

  return (
    <div className="c-validate-widget" test-id="bobcat-validate-widget">
      {/* The Loader mounts <ps-pype> here; it needs a definite height. */}
      <div className="c-validate-widget__pane" ref={containerRef} />
      <TextCaption
        className="c-validate-widget__note"
        test-id="bobcat-validate-widget-status"
      >
        {status === 'error'
          ? `The widget couldn’t load: ${error ?? 'unknown error'}`
          : status === 'booting'
            ? 'Loading the widget…'
            : !chatId
              ? 'Starting the chat…'
              : transcript.length
                ? `${transcript.length} message${transcript.length === 1 ? '' : 's'} read from the logs · saved as a run${done ? ', scored' : ''}`
                : 'Reading the conversation from the logs as it happens…'}
      </TextCaption>
      {view.problem && (
        <p className="c-validate-error" role="alert">
          Couldn’t read the chat from the logs: {view.problem}
        </p>
      )}
    </div>
  );
};
